import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import { ICustomSeriesPaneRenderer, PaneRendererCustomData, PriceToCoordinateConverter, Time } from 'lightweight-charts'
import { HlcAreaSeriesOptions } from './options'
import { ISeriesPaneRenderer } from '../customSeries.typings.ts'
import Chart from '../../chart'
import tinycolor from 'tinycolor2'
import { HlcAreaData } from './data'
import { getOpacityFromHexCode } from '../../../../util/general'

export type HlcAreaBarData = {
    x: number,
    value: HlcAreaData
}

export class HlcAreaSeriesSeriesRenderer<TData extends HlcAreaData>
    implements ICustomSeriesPaneRenderer {
    _data: PaneRendererCustomData<Time, TData> | null = null
    _options: HlcAreaSeriesOptions | null = null

    draw(
        target: CanvasRenderingTarget2D,
        priceConverter: PriceToCoordinateConverter
    ): void {
        target.useBitmapCoordinateSpace(scope =>
            this._drawImpl(scope, priceConverter)
        )
    }

    update(
        data: PaneRendererCustomData<Time, TData>,
        options: HlcAreaSeriesOptions
    ): void {
        this._data = data
        this._options = options
    }

    _drawImpl(
        renderingScope: BitmapCoordinatesRenderingScope,
        priceToCoordinate: PriceToCoordinateConverter
    ): void {
        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return
        }


        const ctx = renderingScope.context;

        const {from, to} = this._data.visibleRange
        const renderLength = to - from;
        
        let previousValue: HlcAreaBarData | null = null;
        ctx.setLineDash([])
        const highLine = new Path2D();
        const lowLine = new Path2D();
        const closeLine = new Path2D();
        
        for (let c=0; c <= renderLength - 1 ; c++) {
            const bar = this._data.bars[c + from];
            const {high, low, close} = bar.originalData;
            const scaledBarX = bar.x*window.devicePixelRatio;
            const highY = priceToCoordinate(high) || 0;
            const lowY = priceToCoordinate(low) || 0;
            const closeY = priceToCoordinate(close) || 0;
            if (!previousValue) {
                previousValue = {
                    x: scaledBarX,
                    value: bar.originalData
                };

                // highAreaPath.moveTo(previousValue.x, highY * window.devicePixelRatio);
                // lowAreaPath.moveTo(previousValue.x, lowY * window.devicePixelRatio);
                highLine.moveTo(previousValue.x, highY * window.devicePixelRatio);
                lowLine.moveTo(previousValue.x, lowY * window.devicePixelRatio);
                closeLine.moveTo(previousValue.x, closeY * window.devicePixelRatio);
            } else {
                
                highLine.lineTo(scaledBarX, highY * window.devicePixelRatio);
                lowLine.lineTo(scaledBarX, lowY * window.devicePixelRatio);
                closeLine.lineTo(scaledBarX, closeY * window.devicePixelRatio);

                previousValue = {
                    x: scaledBarX,
                    value: bar.originalData
                };
            }
        }

        ctx.beginPath();
        let opacity = getOpacityFromHexCode(this._options.highLineColor)
        ctx.strokeStyle = this._options.highLineColor;
        ctx.globalAlpha = opacity;
        ctx.stroke(highLine);
        ctx.closePath();
        ctx.beginPath();
        ctx.strokeStyle = this._options.lowLineColor;
        opacity = getOpacityFromHexCode(this._options.lowLineColor)
        ctx.globalAlpha = opacity;
        ctx.stroke(lowLine);
        ctx.closePath();
        ctx.beginPath();
        opacity = getOpacityFromHexCode(this._options.closeLineColor)
        ctx.globalAlpha = opacity;
        ctx.strokeStyle = this._options.closeLineColor;
        ctx.stroke(closeLine);

        this.createAreaSeries(renderingScope, priceToCoordinate, highLine, lowLine);


        // const lastBar = this._data.bars[renderLength - 1 + from];

        // if (lastBar) {
        //     highAreaPath.addPath(highLine);
        //     lowAreaPath.addPath(lowLine);
        //     const lastX = lastBar.x * window.devicePixelRatio;
        //     const lastCloseY = priceToCoordinate(lastBar.originalData.high) || 0;
        //     const lastLowY = priceToCoordinate(lastBar.originalData.low) || 0;

        //     highAreaPath.lineTo(lastX, lastHighY * window.devicePixelRatio);
        //     lowAreaPath.lineTo(lastX, lastLowY * window.devicePixelRatio);
        // }
        // ctx.closePath();
        
        // previousValue && outline.lineTo(previousValue.x, scaledMidPoint)
        // outline.lineTo(currentStart, scaledMidPoint)
        
        // this.fill(renderingScope, ctx, outline, scaledMidPoint, color === this._options.bottomLineColor ? "bottom" : "top")
        // ctx.closePath();
    }

    createAreaSeries(renderingScope: BitmapCoordinatesRenderingScope, priceToCoordinate: PriceToCoordinateConverter, highLine: Path2D, lowLine: Path2D): void {

        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return
        }


        const ctx = renderingScope.context;

        const highAreaPath = new Path2D();
        const lowAreaPath = new Path2D();

        highAreaPath.addPath(highLine);
        lowAreaPath.addPath(lowLine);

        const {from, to} = this._data.visibleRange
        const renderLength = to - from;


        for (let c=renderLength - 1; c >=0 ; c--) {
            const bar = this._data.bars[c + from];
            const {close} = bar.originalData;
            const scaledBarX = bar.x*window.devicePixelRatio;
            const closeY = (priceToCoordinate(close) || 0 ) * window.devicePixelRatio;

            highAreaPath.lineTo(scaledBarX, closeY);
            lowAreaPath.lineTo(scaledBarX, closeY);
        }

        let opacity = getOpacityFromHexCode(this._options.topFillColor)
        ctx.globalAlpha = opacity;
        ctx.fillStyle = this._options.topFillColor;
        ctx.fill(highAreaPath);

        opacity = getOpacityFromHexCode(this._options.bottomFillColor)
        ctx.globalAlpha = opacity;
        ctx.fillStyle = this._options.bottomFillColor;
        ctx.fill(lowAreaPath);
    }
}
